// Google Tag Manager tracking utilities
// This will work alongside the existing crmIntegration.ts analytics

export interface GTMEvent {
  event: string;
  event_category?: string;
  event_action?: string;
  event_label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

// Enhanced GTM event tracking
export const trackGTMEvent = (eventData: GTMEvent) => {
  if (typeof window !== 'undefined' && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: eventData.event,
      event_category: eventData.event_category,
      event_action: eventData.event_action,
      event_label: eventData.event_label,
      value: eventData.value,
      ...eventData.custom_parameters
    });
    
    console.log('📊 GTM Event Tracked:', eventData);
  } else {
    console.warn('⚠️ GTM dataLayer not available');
  }
};

// Specific tracking functions for your career site
export const trackModalOpen = (modalType: string, source: string) => {
  trackGTMEvent({
    event: 'modal_open',
    event_category: 'engagement',
    event_action: 'open_modal',
    event_label: modalType,
    custom_parameters: {
      modal_type: modalType,
      source: source,
      timestamp: new Date().toISOString()
    }
  });
};

export const trackModalClose = (modalType: string, completed: boolean = false) => {
  trackGTMEvent({
    event: 'modal_close',
    event_category: 'engagement',
    event_action: completed ? 'complete_modal' : 'abandon_modal',
    event_label: modalType,
    custom_parameters: {
      modal_type: modalType,
      completed: completed,
      timestamp: new Date().toISOString()
    }
  });
};

export const trackFormStep = (formType: string, step: number, totalSteps: number) => {
  trackGTMEvent({
    event: 'form_step',
    event_category: 'form_interaction',
    event_action: 'step_progress',
    event_label: `${formType}_step_${step}`,
    value: step,
    custom_parameters: {
      form_type: formType,
      step: step,
      total_steps: totalSteps,
      progress_percentage: Math.round((step / totalSteps) * 100),
      timestamp: new Date().toISOString()
    }
  });
};

export const trackFormSubmission = (formType: string, success: boolean, source: string) => {
  trackGTMEvent({
    event: 'form_submission',
    event_category: 'conversion',
    event_action: success ? 'successful_submission' : 'failed_submission',
    event_label: formType,
    value: getFormValue(formType),
    custom_parameters: {
      form_type: formType,
      success: success,
      source: source,
      timestamp: new Date().toISOString()
    }
  });
};

export const trackButtonClick = (buttonText: string, location: string, destination?: string) => {
  trackGTMEvent({
    event: 'button_click',
    event_category: 'engagement',
    event_action: 'click_button',
    event_label: buttonText,
    custom_parameters: {
      button_text: buttonText,
      location: location,
      destination: destination,
      timestamp: new Date().toISOString()
    }
  });
};

export const trackPageSection = (sectionName: string, action: string = 'view') => {
  trackGTMEvent({
    event: 'section_interaction',
    event_category: 'page_engagement',
    event_action: action,
    event_label: sectionName,
    custom_parameters: {
      section_name: sectionName,
      action: action,
      timestamp: new Date().toISOString()
    }
  });
};

// Helper function to assign values to different form types for conversion tracking
const getFormValue = (formType: string): number => {
  switch (formType) {
    case 'application':
      return 100; // High value for full applications
    case 'info_request':
      return 25;  // Medium value for info requests
    case 'recruiter_contact':
      return 50;  // Higher value for direct contact
    default:
      return 10;  // Default value
  }
};

// Track user engagement milestones
export const trackEngagementMilestone = (milestone: string, value?: number) => {
  trackGTMEvent({
    event: 'engagement_milestone',
    event_category: 'user_engagement',
    event_action: 'milestone_reached',
    event_label: milestone,
    value: value,
    custom_parameters: {
      milestone: milestone,
      timestamp: new Date().toISOString()
    }
  });
};

// Track scroll depth for page engagement
export const trackScrollDepth = (percentage: number) => {
  trackGTMEvent({
    event: 'scroll_depth',
    event_category: 'page_engagement',
    event_action: 'scroll',
    event_label: `${percentage}%`,
    value: percentage,
    custom_parameters: {
      scroll_percentage: percentage,
      timestamp: new Date().toISOString()
    }
  });
};